import { fetchAuthSession } from 'aws-amplify/auth'
import { awsCredentialsProvider } from 'app/app'
import { AwsCredentials, MsalCredentials } from 'app/models/domain/auth'
import {
    AuthenticationResult,
    InteractionRequiredAuthError,
} from '@azure/msal-browser'
import { useDispatch } from 'react-redux'
import { clearQrState } from 'app/store/slices/qrAuthSlice'
import { clearAuthState } from 'app/store/slices/authSlice'
import { msalInstance } from '../msal'
import { ApiHelper } from 'app/api/api-helper'
import { ApiRequester } from 'app/api/api-requestor'
import { TuhiUser } from 'app/models/domain/user'
import { FetchUserInfoResponse } from 'app/api/types'
import { initialiseAmplifyWithTokenProvider } from '../TuhiTokenProvider'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { loginRequest } from 'app/authConfig'
import { TuhiUserDto } from 'app/models/generated'
import { logAnalytics } from 'app/logging/analytics'
import {
    AuthenticationAnalyticHelper,
    AuthenticationAnalytic,
} from 'app/logging/analytics/events/authenticationAnalytics'
import { logEvent } from 'app/logging/firebase'
import { logger } from 'app/logging/Logger'

enum Claim {
    confidenceLevel = 'urn:login:health:nz:claims:confidence_level',
    exp = 'exp',
}

export interface AuthenticationResponse {
    success: boolean
    error?: string
    msalCredentials?: MsalCredentials
    tuhiUser?: TuhiUser
    awsCredentials?: AwsCredentials
}

export const acquireSilentToken = async (
    forceRefresh: boolean = false
): Promise<AuthenticationResult | null> => {
    try {
        const scopes = loginRequest.scopes.filter(
            (s): s is string => s !== undefined
        )
        return await msalInstance.acquireTokenSilent({
            scopes,
            forceRefresh: forceRefresh,
        })
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            window.location.href = `${window.location.origin}/login/`
        } else {
            logger.error('Unexpected token error', error as Error, {
                action: 'acquire_silent_token',
                metadata: { forceRefresh },
            })
        }
        return null
    }
}

export const handleRefreshToken = async (
    forceRefresh: boolean,
    maxRetries = 5,
    baseDelayMs = 1000
): Promise<AuthenticationResponse> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const authenticationResult = await acquireSilentToken(forceRefresh)
            if (authenticationResult) {
                return await handleAuthenticationResult(
                    authenticationResult,
                    forceRefresh
                )
            }
        } catch (error) {
            logEvent(
                'ERROR',
                'refresh_token_failed',
                'Failed to refresh tokens',
                {
                    error,
                    attempt,
                }
            )

            if (attempt === maxRetries) {
                // Give up after max retries
                throw error
            }

            // Exponential backoff with jitter: baseDelay * 2^(attempt-1) + random(0, 250ms)
            const delay =
                baseDelayMs * Math.pow(2, attempt - 1) +
                Math.floor(Math.random() * 250)

            await new Promise((resolve) => setTimeout(resolve, delay))
        }
    }

    // If we get here, all retries failed
    return {
        success: false,
        error: 'Failed to refresh token after maximum retries',
    }
}

export const restoreLoginSessionOnReload = async (
    onRestore: (authResponse: AuthenticationResponse) => void,
    onRestoreFailed: () => void,
    onRestoreStart: () => void,
    onRestoreEnd: () => void
) => {
    let authenticationResult: AuthenticationResult | null = null

    if (msalInstance.getAllAccounts().length < 1) {
        authenticationResult = await msalInstance.handleRedirectPromise()
    } else {
        authenticationResult = await acquireSilentToken()
    }

    if (!authenticationResult) {
        onRestoreFailed()
        return
    }

    onRestoreStart()

    const authResponse = await handleAuthenticationResult(authenticationResult)

    onRestore(authResponse)
    onRestoreEnd()
}

export const useSignOut = () => {
    const dispatch = useDispatch()

    return async () => {
        // await msalInstance.logoutRedirect({ account })
        await msalInstance.logoutRedirect()
        await msalInstance.clearCache()
        dispatch(clearQrState())
        dispatch(clearAuthState())
        sessionStorage.clear()

        const event = AuthenticationAnalyticHelper.makeEvent(
            AuthenticationAnalytic.Logout
        )
        logAnalytics(event)
    }
}

export const federateWithIdToken = async (
    token: string,
    forceRefresh: boolean = false
) => {
    const domain = process.env.OIDC_PROVIDER
    if (!domain) {
        throw new Error('OIDC_PROVIDER environment variable is not set')
    }

    awsCredentialsProvider.loadFederatedLogin({
        domain,
        token: token,
    })

    const authSession = await fetchAuthSession({ forceRefresh: forceRefresh })

    return authSession.credentials
}

export async function fetchTuhiUser(accessToken: string): Promise<TuhiUser> {
    const operation = ApiHelper.buildFetchUserInfoOperation(accessToken)
    const userInfo =
        await ApiRequester.callGetOperationRequest<FetchUserInfoResponse>(
            operation
        )

    // The API returns UserInfo, but we need to fetch the full TuhiUserDto from DataStore
    // For now, we'll use a type assertion to work around the mismatch
    const tuhiUserDto = userInfo as unknown as TuhiUserDto

    return await TuhiUser.buildFromDto(tuhiUserDto)
}

export async function handleAuthenticationResult(
    authResult: AuthenticationResult,
    forceRefresh: boolean = false
): Promise<AuthenticationResponse> {
    const awsCredentialsRaw = await federateWithIdToken(
        authResult.idToken,
        forceRefresh
    )

    // Convert AWS credentials expiration from Date to ISO string for Redux serialization
    const awsCredentials: AwsCredentials | undefined = awsCredentialsRaw
        ? {
              ...awsCredentialsRaw,
              expiration: awsCredentialsRaw.expiration?.toISOString(),
          }
        : undefined

    const tuhiUser = await fetchTuhiUser(authResult.accessToken)

    initialiseAmplifyWithTokenProvider(
        authResult.idToken,
        authResult.accessToken,
        tuhiUser.id
    )

    const { confidenceLevel } = buildUserCredentials(authResult.accessToken)

    const msalCredentials: MsalCredentials = {
        idToken: authResult.idToken,
        accessToken: authResult.accessToken,
        confidenceLevel: confidenceLevel,
        expiration: (authResult.expiresOn ?? new Date()).toISOString(),
    }

    return {
        success: true,
        msalCredentials: msalCredentials,
        tuhiUser: tuhiUser,
        awsCredentials,
    }
}

export const buildUserCredentials = (accessToken: string) => {
    const decodedJwt = jwtDecode(accessToken) as JwtPayload & {
        [Claim.confidenceLevel]: number
    }

    const sub = decodedJwt.sub
    const exp = decodedJwt.exp
    const confidenceLevel = decodedJwt[Claim.confidenceLevel]

    return { sub, confidenceLevel, exp }
}
